/**
 * A class for creating mutable properties that can change on window resize. <br><br>
 *
 * There are two ways to change properties:
 * - To set a resize-listener on window (or use {@linkcode Viewport}).
 * When the window is resized, change the properties with the help of
 * {@linkcode MutableProps.changeProps}
 *
 * - The second way is to use the MutableProps and add responsive properties
 * with help of {@linkcode MutableProps.addResponsiveProps}.
 *
 * @example
 *
 * interface IStatic {
 *   static: string;
 * }

 * interface IChangeable {
 *   changeable: string;
 * }

 * const props = new MutableProps<IStatic, IChangeable>({
 *   static: '',
 *   changeable: 'something',
 * });

 * props.addResponsiveProps({
 *   breakpoint: 'viewport_phone',
 *   settings: {
 *     changeable: 'phone',
 *   },
 * });

 * props.changeProps({ changeable: 'changed' });
 */
export class MutableProps {
    /**
     * Get current properties
     */
    get props() {
        return this._props;
    }
    /**
     * @example
     *
     * const static = {
     *   myProp: true,
     * };
     *
     * const responsive = [
     *   {
     *     breakpoint: 'm',
     *     settings: {
     *       myProp: false
     *     }
     *   }
     * ];
     *
     * const props = new MutableProps(static, responsive);
     */
    constructor(
    /**
     * The properties that were set while initialization.
     * These properties will nevet change.
     */
    _initProps, 
    /**
     * A callback that is launched when properties are changed
     */
    _onMutate = () => { }, 
    /**
     * Name of the responsive properties.
     */
    _name = 'Responsive Props') {
        this._initProps = _initProps;
        this._onMutate = _onMutate;
        this._name = _name;
        /**
         * A set of responsive rules
         */
        this._responsiveRules = [];
        this._app = window.vevetApp;
        this._refProps = Object.assign({}, _initProps);
        this._props = Object.assign({}, _initProps);
        this._activeBreakpoints = [];
    }
    /**
     * Add responsive rules
     */
    addResponsiveProps(rules) {
        this._responsiveRules.push(rules);
        this._responseProps();
        if (typeof this._viewportCallback !== 'undefined') {
            return;
        }
        this._viewportCallback = this._app.viewport.add('width', this._responseProps.bind(this), { name: this._name });
    }
    /**
     * Change properties according to the "responsive" settings
     */
    _responseProps() {
        const app = this._app;
        const { viewport } = app;
        let newProps = false;
        const statProp = Object.assign({}, this._refProps);
        const prevActiveBreakpointsString = [...this._activeBreakpoints].join('_');
        this._activeBreakpoints = [];
        // go through all breakpoints
        // and check if a proper breakpoint exists
        this._responsiveRules.forEach(({ settings, breakpoint }) => {
            if (typeof breakpoint === 'number') {
                if (viewport.width <= breakpoint) {
                    this._activeBreakpoints.push(breakpoint);
                    newProps = Object.assign(Object.assign({}, statProp), settings);
                }
            }
            else if (typeof breakpoint === 'string') {
                // viewport size
                if ((breakpoint === 'viewport_desktop' && viewport.isDesktop) ||
                    (breakpoint === 'viewport_tablet' && viewport.isTablet) ||
                    (breakpoint === 'viewport_phone' && viewport.isPhone)) {
                    this._activeBreakpoints.push(breakpoint);
                    newProps = Object.assign(Object.assign({}, (newProps || statProp)), settings);
                }
                // device type
                if ((breakpoint === 'device_phone' && app.isPhone) ||
                    (breakpoint === 'device_tablet' && app.isTablet) ||
                    (breakpoint === 'device_mobile' && app.isMobile)) {
                    this._activeBreakpoints.push(breakpoint);
                    newProps = Object.assign(Object.assign({}, (newProps || statProp)), settings);
                }
            }
        });
        // check if properties really changed
        const activeBreakpointsString = this._activeBreakpoints.join('_');
        const isPropsChanged = activeBreakpointsString !== prevActiveBreakpointsString;
        // if there's no breakpoint, restore the props
        if (!newProps) {
            this._props = Object.assign(Object.assign({}, this._props), this._refProps);
        }
        else {
            // otherwise, change the properties
            this._props = Object.assign(Object.assign({}, this._props), newProps);
        }
        // callback
        if (isPropsChanged) {
            this._onMutate();
        }
    }
    /**
     * This method allows you to change the properties manually.
     */
    changeProps(props) {
        this._props = Object.assign(Object.assign({}, this._props), props);
        this._refProps = Object.assign(Object.assign({}, this._refProps), props);
        this._onMutate();
    }
    /**
     * Destroy the responsive properties.
     */
    destroy() {
        if (this._viewportCallback) {
            this._viewportCallback.remove();
        }
    }
}
//# sourceMappingURL=index.js.map